import React, { useCallback, useEffect, useMemo, useState } from "react";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { qformat, DATE_FORMATS } from "views_shared/app/javascript/utils/dateFormatters";
import OutletRating from "../shared/OutletRating";
import SearchableTable from "../shared/SearchableTable";
import { createPostgRESTDataAdapter } from "../shared/dataAdapters";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;

// --- Summary card definitions

const SUMMARY_CARDS = [
  { key: "all", label: "Total Outlets", accent: "#6c757d", filter: {} },
  {
    key: "approve_and_notify",
    label: "Approve+Feed & Notify",
    accent: "#198754",
    filter: { auto_action: "eq.approve_and_notify" },
  },
  {
    key: "approve_and_distribute",
    label: "Approve+Feed",
    accent: "#0d6efd",
    filter: { auto_action: "eq.approve_and_distribute" },
  },
  { key: "approve", label: "Approve", accent: "#fd7e14", filter: { auto_action: "eq.approve" } },
  { key: "ghost", label: "Ghost", accent: "#6c757d", filter: { auto_action: "eq.ghost" } },
  { key: "manual_review", label: "Manual Review", accent: "#6c757d", filter: { auto_action: "eq.manual_review" } },
  {
    key: "no_config",
    label: "No Config",
    accent: "#dc3545",
    filter: { auto_action: "is.null", request_count: "gt.0" },
  },
];

// --- Radio filter definitions

const RADIO_OPTIONS = [
  { key: "all", label: "All" },
  { key: "approve_and_notify", label: "Approve+Feed & Notify" },
  { key: "approve_and_distribute", label: "Approve+Feed" },
  { key: "approve", label: "Approve" },
  { key: "ghost", label: "Ghost" },
  { key: "manual_review", label: "Manual" },
  { key: "no_config", label: "No Config", danger: true },
];

const RADIO_FILTERS = {
  all: {},
  approve_and_notify: { auto_action: "eq.approve_and_notify" },
  approve_and_distribute: { auto_action: "eq.approve_and_distribute" },
  approve: { auto_action: "eq.approve" },
  ghost: { auto_action: "eq.ghost" },
  manual_review: { auto_action: "eq.manual_review" },
  no_config: { auto_action: "is.null", request_count: "gt.0" },
};

// --- Columns for SearchableTable

const COLUMNS = {
  id: { type: "number", hidden: true },
  name: { type: "string", hidden: true },
  display_name: { type: "string", display_as: "Outlet" },
  profile_image_url: { type: "string", hidden: true },
  request_count: { type: "number", hidden: true },
  domain: { type: "string", display_as: "Domain" },
  prestige: { type: "number", hidden: true },
  domain_authority: { type: "number", display_as: "Rating" },
  auto_action: { type: "string", display_as: "Auto Action" },
  set_free: { type: "boolean", hidden: true },
  set_by_name: { type: "string", display_as: "Set By" },
  set_at: { type: "datetime", hidden: true },
  config_id: { type: "number", hidden: true },
  slug: { type: "string", hidden: true },
  pending_count: { type: "number", hidden: true },
};

// --- Formatting helpers

function actionBadge(autoAction) {
  switch (autoAction) {
    case "approve_and_notify":
      return (
        <span
          className="badge"
          style={{ border: "1px solid #198754", color: "#198754", backgroundColor: "transparent" }}
        >
          Approve+Feed & Notify
        </span>
      );
    case "approve":
      return (
        <span
          className="badge"
          style={{ border: "1px solid #fd7e14", color: "#fd7e14", backgroundColor: "transparent" }}
        >
          Approve
        </span>
      );
    case "approve_and_distribute":
      return (
        <span
          className="badge"
          style={{ border: "1px solid #0d6efd", color: "#0d6efd", backgroundColor: "transparent" }}
        >
          Approve+Feed
        </span>
      );
    case "ghost":
      return (
        <span
          className="badge"
          style={{ border: "1px solid #6c757d", color: "#6c757d", backgroundColor: "transparent" }}
        >
          Ghost
        </span>
      );
    case "manual_review":
      return (
        <span
          className="badge"
          style={{ border: "1px solid #6c757d", color: "#6c757d", backgroundColor: "transparent" }}
        >
          Manual Review
        </span>
      );
    default:
      return null;
  }
}

// --- Summary cards component

function OutletSummaryCards({ counts }) {
  return (
    <div className="row g-3 mb-4">
      {SUMMARY_CARDS.map((card) => (
        <div key={card.key} className="col">
          <div className="card h-100" style={{ borderLeft: `4px solid ${card.accent}` }}>
            <div className="card-body py-3 text-center">
              <div className="fs-3 fw-bold">{counts[card.key] ?? "\u2014"}</div>
              <div className="text-muted small">{card.label}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Filter bar component

function OutletFilterBar({ activeRadio, onRadioChange }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
      <span className="text-muted small">Filter:</span>
      <div className="btn-group" role="group">
        {RADIO_OPTIONS.map((opt) => (
          <React.Fragment key={opt.key}>
            <input
              type="radio"
              className="btn-check"
              name="outletFilter"
              id={`filter-${opt.key}`}
              checked={activeRadio === opt.key}
              onChange={() => onRadioChange(opt.key)}
            />
            <label
              className={`btn btn-sm ${opt.danger ? "btn-outline-danger" : "btn-outline-secondary"}`}
              htmlFor={`filter-${opt.key}`}
            >
              {opt.label}
            </label>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// --- Main component

function MediaOutletsIndex() {
  const [activeRadio, setActiveRadio] = useState("all");
  const [counts, setCounts] = useState({});

  // Fetch summary counts via parallel HEAD requests
  const fetchCounts = useCallback(async () => {
    const headers = {
      ...authenticatedHeadersHash(),
      Prefer: "count=exact",
    };
    const base = `${SUPABASE_URL()}/rest/v1/admin_outlets`;

    const results = await Promise.all(
      SUMMARY_CARDS.map(async (card) => {
        const url = new URL(base);
        url.searchParams.set("select", "count");
        Object.entries(card.filter).forEach(([key, val]) => url.searchParams.set(key, val));

        try {
          const res = await fetch(url.toString(), { method: "HEAD", headers });
          if (!res.ok) return { key: card.key, count: null };
          const cr = res.headers.get("Content-Range");
          const total = cr?.split("/")[1];
          return { key: card.key, count: total ? parseInt(total, 10) : 0 };
        } catch {
          return { key: card.key, count: null };
        }
      }),
    );

    const newCounts = {};
    results.forEach((result) => {
      newCounts[result.key] = result.count;
    });
    setCounts(newCounts);
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Recreate adapter when radio filter changes
  const dataAdapter = useMemo(() => {
    const headers = authenticatedHeadersHash();
    return createPostgRESTDataAdapter({
      endpoint: `${SUPABASE_URL()}/rest/v1`,
      resource: "admin_outlets",
      headers,
      staticFilters: RADIO_FILTERS[activeRadio],
      columns: COLUMNS,
      searchColumns: ["display_name", "domain"],
    });
  }, [activeRadio]);

  const renderCell = (row, column) => {
    if (column === "display_name") {
      return (
        <a href={`/admin/media_outlets/${row.id}`} className="text-decoration-none">
          {row.profile_image_url && (
            <img
              src={row.profile_image_url}
              alt=""
              style={{ maxWidth: 80, maxHeight: 32, objectFit: "contain" }}
              className="d-block mb-1"
            />
          )}
          <div>
            <strong>{row.display_name}</strong>
            <div className="text-muted small">{row.request_count} requests</div>
          </div>
        </a>
      );
    }

    if (column === "domain") {
      return row.domain ? <code className="small">{row.domain}</code> : "\u2014";
    }

    if (column === "domain_authority") {
      if (row.domain_authority == null && !(row.prestige > 0)) {
        return <span className="text-muted">{"\u2014"}</span>;
      }
      return <OutletRating prestige={row.prestige} domainAuthority={row.domain_authority} />;
    }

    if (column === "auto_action") {
      return (
        <div className="d-flex gap-1 align-items-center flex-wrap">
          {row.auto_action ? actionBadge(row.auto_action) : <span className="badge bg-danger">No Config</span>}
          {row.set_free && <span className="badge border border-info text-info">Free</span>}
        </div>
      );
    }

    if (column === "set_by_name") {
      if (!row.set_by_name) return <span className="text-muted">{"\u2014"}</span>;
      return (
        <div>
          <div className="small">{row.set_by_name}</div>
          {row.set_at && <div className="text-muted small">{qformat(row.set_at, DATE_FORMATS.date)}</div>}
        </div>
      );
    }

    return undefined;
  };

  return (
    <div>
      <h1 className="h3 mb-4">Media Outlets</h1>

      <OutletSummaryCards counts={counts} />

      <OutletFilterBar activeRadio={activeRadio} onRadioChange={setActiveRadio} />

      <SearchableTable
        columns={COLUMNS}
        searchColumns={["name", "domain"]}
        dataAdapter={dataAdapter}
        pageSize={25}
        defaultSort={{ request_count: "desc" }}
        renderCell={renderCell}
        enableFiltering={false}
      />
    </div>
  );
}

export default MediaOutletsIndex;
